import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React from "react";
import { useStores } from "../../../use-stores";
import { UrgentSourcingCatalogProductForm } from "../../../models/UrgentSourcing/UrgentSourcing";
import { ProductCategoriesMapping } from "../../../models/Products/Product";
import { IconButton, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import CustomTypography from "../../components/common/form/CustomTypography";
import { Box } from "@mui/material";
import CustomTextField from "../../components/common/textFields/TextField";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { TextField } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import { Close as CloseIcon } from "@mui/icons-material";
import AppDropDown from "../../components/common/filter/AppDropDown";

interface UrgentSourcingAddCatalogProductPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UrgentSourcingCatalogProductForm) => void;
  categoriesList: ProductCategoriesMapping[];
}

const UrgentSourcingAddCatalogProductPopup: React.FC<
  UrgentSourcingAddCatalogProductPopupProps
> = ({ open, onClose, onSave, categoriesList }) => {
  const { urgentSourcingStore } = useStores();
  const theme = useTheme();

  const isFormValid = () => {
    return (
      urgentSourcingStore.catalogProductFormData.categoryId &&
      urgentSourcingStore.catalogProductFormData.sellingPrice &&
      urgentSourcingStore.catalogProductFormData.display &&
      urgentSourcingStore.catalogProductFormData.description
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            pt: 2,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            {`Add Catalog Product  (${urgentSourcingStore.catalogProductFormData.catalogueId})`}
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            scrollBehavior: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Categories{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown
                isMultiSelect={false}
                title="Select Category"
                disabled={false}
                list={categoriesList.map((category) => ({
                  id: String(category.category_id),
                  value: category.category_name || "",
                }))}
                fontSize="14px"
                local={true}
                borderColor={theme.palette.customColors.greyBackground}
                value={
                  urgentSourcingStore.catalogProductFormData.categoryId
                    ? [
                        {
                          id: String(
                            urgentSourcingStore.catalogProductFormData
                              .categoryId
                          ),
                          value:
                            categoriesList.find(
                              (category) =>
                                String(category.category_id) ===
                                String(
                                  urgentSourcingStore.catalogProductFormData
                                    .categoryId
                                )
                            )?.category_name || "",
                        },
                      ]
                    : []
                }
                handleSelect={(value) =>
                  urgentSourcingStore.updateCatalogProductField(
                    "categoryId",
                    value[0]?.id || ""
                  )
                }
              />
            </Box>

            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Selling Price{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTextField
                width="100%"
                type="number"
                placeholder="Enter selling price"
                value={urgentSourcingStore.catalogProductFormData.sellingPrice}
                onChange={(e) =>
                  urgentSourcingStore.updateCatalogProductField(
                    "sellingPrice",
                    e.target.value
                  )
                }
              />
            </Box>

            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Display{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <AppDropDown
                title="Select Display Status"
                placeholderText=""
                fontSize="0.7rem"
                isMultiSelect={false}
                value={
                  urgentSourcingStore.catalogProductFormData.display?.toString() ||
                  ""
                }
                list={[
                  { id: "1", value: "Yes" },
                  { id: "0", value: "No" },
                ]}
                menuZIndex={10000}
                onSingleSelectionChange={(id, name) =>
                  urgentSourcingStore.updateCatalogProductField("display", id)
                }
              />
            </Box>

            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Description{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <TextField
                multiline={true}
                rows={2}
                fullWidth
                placeholder="Enter product description"
                value={urgentSourcingStore.catalogProductFormData.description}
                onChange={(e) =>
                  urgentSourcingStore.updateCatalogProductField(
                    "description",
                    e.target.value
                  )
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            // flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              // minWidth: { xs: "100%", sm: "auto" },
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            label="Add Product"
            onClick={() => onSave(urgentSourcingStore.catalogProductFormData)}
            variant="primary"
            disabled={!isFormValid()}
            customStyles={{
              backgroundColor: isFormValid()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: isFormValid()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: isFormValid() ? "translateY(-1px)" : "none",
                boxShadow: isFormValid()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(UrgentSourcingAddCatalogProductPopup);
